import {
  Camera,
  CaretDown,
  Copy,
  Export,
  Funnel,
  MagnifyingGlass,
  Plus,
  ShareNetwork,
  Star,
  Table,
  Trash,
} from "@phosphor-icons/react"
import { cn } from "@tokenterminal/core/utils/cn"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

const schemaData = [
  { field: "block_slot", type: "INTEGER", mode: "NULLABLE" },
  { field: "block_hash", type: "STRING", mode: "NULLABLE" },
  { field: "block_height", type: "INTEGER", mode: "NULLABLE" },
  { field: "block_timestamp", type: "TIMESTAMP", mode: "NULLABLE" },
  { field: "parent_slot", type: "INTEGER", mode: "NULLABLE" },
  { field: "previous_block_hash", type: "STRING", mode: "NULLABLE" },
  { field: "total_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  { field: "successful_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  { field: "failed_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  { field: "vote_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  { field: "non_vote_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  {
    field: "successful_vote_transaction_count",
    type: "INTEGER",
    mode: "NULLABLE",
  },
  {
    field: "successful_non_vote_transaction_count",
    type: "INTEGER",
    mode: "NULLABLE",
  },
  { field: "failed_vote_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  {
    field: "failed_non_vote_transaction_count",
    type: "INTEGER",
    mode: "NULLABLE",
  },
  { field: "total_fee", type: "INTEGER", mode: "NULLABLE" },
  {
    field: "successful_vote_transactions_fee",
    type: "INTEGER",
    mode: "NULLABLE",
  },
]

const tabs = [
  { label: "Schema", active: true },
  { label: "Details", active: false },
  { label: "Preview", active: false },
  { label: "Table Explorer", active: false },
  { label: "Preview", active: false, variant: "button" },
  { label: "Insights", active: false },
  { label: "Lineage", active: false },
  { label: "Data Profile", active: false },
  { label: "Data Quality", active: false },
]

export function SchemaViewer() {
  return (
    <div className="relative" style={FADE_MASK_STYLE}>
      <div
        className="bg-white dark:bg-[#0a0a0a] border border-neutral-200 dark:border-neutral-800 rounded-lg"
        style={{ width: "calc(100% + 300px)", minWidth: "1200px" }}
      >
        {/* Top Navigation Bar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 dark:border-neutral-800">
          {/* Left side - window controls and tabs */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <button className="w-3 h-3 rounded-full bg-neutral-300 dark:bg-neutral-700" />
              <CaretDown className="w-3 h-3 text-neutral-400 dark:text-neutral-500" />
            </div>
            <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-700" />
            <div className="flex items-center gap-1 px-2 py-1 bg-neutral-100 dark:bg-neutral-800 rounded-sm">
              <Table className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">
                blocks
              </span>
              <CaretDown className="w-3 h-3 text-neutral-400 dark:text-neutral-500 ml-1" />
            </div>
            <button className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <Plus className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
            </button>
            <CaretDown className="w-3 h-3 text-neutral-400 dark:text-neutral-500" />
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 dark:border-neutral-800 text-sm">
          <span className="text-blue-500 dark:text-blue-400">blockchain</span>
          <span className="text-neutral-400 dark:text-neutral-500">/</span>
          <span className="text-neutral-500 dark:text-neutral-400">
            Datasets
          </span>
          <span className="text-neutral-400 dark:text-neutral-500">/</span>
          <span className="text-blue-500 dark:text-blue-400">solana</span>
          <span className="text-neutral-400 dark:text-neutral-500">/</span>
          <span className="text-neutral-500 dark:text-neutral-400">Tables</span>
          <span className="text-neutral-400 dark:text-neutral-500">/</span>
          <span className="text-neutral-700 dark:text-neutral-300 font-medium">
            blocks
          </span>
        </div>

        {/* Title Bar with Actions */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
            <span className="text-lg font-medium text-neutral-800 dark:text-neutral-200">
              blocks
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <MagnifyingGlass className="w-4 h-4" />
              Query
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              Open in
              <CaretDown className="w-3 h-3" />
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <ShareNetwork className="w-4 h-4" />
              Share
              <CaretDown className="w-3 h-3" />
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <Copy className="w-4 h-4" />
              Copy
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <Camera className="w-4 h-4" />
              Snapshot
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <Trash className="w-4 h-4" />
              Delete
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
              <Export className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 px-4 border-b border-neutral-200 dark:border-neutral-800">
          {tabs.map((tab) => (
            <button
              key={`${tab.label}-${tab.variant || "tab"}`}
              className={cn(
                "px-4 py-3 text-sm border-b-2",
                tab.active
                  ? "text-blue-500 dark:text-blue-400 border-blue-500 dark:border-blue-400"
                  : "text-neutral-500 dark:text-neutral-400 border-transparent hover:text-neutral-700 dark:hover:text-neutral-300",
                tab.variant === "button" &&
                  "bg-neutral-100 dark:bg-neutral-800 rounded-sm mx-1"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
          <Funnel className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            Filter
          </span>
          <input
            type="text"
            placeholder="Enter property name or value"
            className="flex-1 bg-transparent text-sm text-neutral-500 dark:text-neutral-400 placeholder-neutral-400 dark:placeholder-neutral-600 outline-hidden"
          />
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-[32px_1fr_120px_100px_140px_80px_100px_120px_120px_80px] gap-0 px-4 py-2 border-b border-neutral-200 dark:border-neutral-800 text-xs text-neutral-500 dark:text-neutral-400">
          <div className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 rounded-sm border-neutral-300 dark:border-neutral-600 bg-transparent"
            />
          </div>
          <div>Field name</div>
          <div>Type</div>
          <div>Mode</div>
          <div>Description</div>
          <div>Key</div>
          <div>Collation</div>
          <div>Default Value</div>
          <div>Policy Tags</div>
          <div>Da...</div>
        </div>

        {/* Table Rows */}
        <div className="flex flex-col">
          {schemaData.map((row, _index) => (
            <div
              key={row.field}
              className="grid grid-cols-[32px_1fr_120px_100px_140px_80px_100px_120px_120px_80px] gap-0 px-4 py-2.5 border-b border-neutral-200/50 dark:border-neutral-800/50 text-sm hover:bg-neutral-100/50 dark:hover:bg-neutral-800/30"
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded-sm border-neutral-300 dark:border-neutral-600 bg-transparent"
                />
              </div>
              <div className="text-neutral-700 dark:text-neutral-300 font-mono text-xs">
                {row.field}
              </div>
              <div className="text-neutral-500 dark:text-neutral-400 font-mono text-xs">
                {row.type}
              </div>
              <div className="text-neutral-500 dark:text-neutral-400 text-xs">
                {row.mode}
              </div>
              <div className="text-neutral-400 dark:text-neutral-500">-</div>
              <div className="text-neutral-400 dark:text-neutral-500">-</div>
              <div className="text-neutral-400 dark:text-neutral-500">-</div>
              <div className="text-neutral-400 dark:text-neutral-500">-</div>
              <div className="text-neutral-400 dark:text-neutral-500">-</div>
              <div className="text-neutral-400 dark:text-neutral-500">-</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
